import React, { useContext, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { Agent } from '@moderepo/mode-internal-apis';
import { ritzDataContext, selectLoggedInAgent, selectProjectById }
    from '@moderepo/mode-data-state-ritz';
import { useRitzTranslation } from '../../utils/customHooks';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';
import { createComponents } from '../../utils/ritzUIExtensionUtils';
import { RouteHelper } from '..';


/**
 * This is the page where we show the Project's most important UI components. This is the FIRST page that an agent would see when they
 * selected a project.
 */
export const ProjectDashboardPage: React.FC = () => {
    const params = useParams();
    const projectId = Number(params.projectId);
    const { state:dataState } = useContext(ritzDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);
    const navigate = useNavigate();

    const projectUIConfig = useMemo(() => {
        if (project) {
            return ProjectTypeHandlersManager.getInstance()
                .getProjectCustomUIHandler(project.supportToolSettings.projectType)
                ?.getCustomUIConfig(loggedInAgent, project);
        }
        return undefined;
    }, [loggedInAgent, project]);


    // Get the projectDashboardConfig based on the current project
    const projectDashboardConfig = useMemo(() => {
        return projectUIConfig?.projectDashboardPage;
    }, [projectUIConfig?.projectDashboardPage]);


    /**
     * If the project's uiConfig contain the setting for "landingPage" then redirect the user to that page.
     */
    if (projectUIConfig?.landingPage) {
        const url = trans(projectUIConfig.landingPage, { projectId });

        // Check if URL contains /projects/{{projectId}}. If not, add it to the URL.
        const defaultProjectLandingPage = RouteHelper.getInstance(navigate).getProjectHomePageUrl(projectId);
        const fixedUrl = !url.startsWith(defaultProjectLandingPage)
            ? `${defaultProjectLandingPage}${url.startsWith('/') ? '' : '/'}${url}`
            : url;

        return (
            <Navigate
                to={fixedUrl}
                replace={true}
            />
        );
    }


    return (
        <div className={clsx('page-main-content', projectDashboardConfig?.className)} style={projectDashboardConfig?.style}>
            {project ? (
                <>
                    {(projectDashboardConfig?.titleKey || projectDashboardConfig?.subtitleKey) && (
                        <div className="title-bar">
                            <div className="title-bar-item title-text">
                                <div className="page-title">
                                    {trans(projectDashboardConfig?.titleKey || 'page.title.dashboard')}
                                </div>
                                <div className="page-subtitle">
                                    {trans(projectDashboardConfig?.subtitleKey || 'page.subtitle.dashboard')}
                                </div>
                            </div>
                        </div>
                    )}

                    {projectDashboardConfig?.components && (
                        createComponents(projectDashboardConfig.components, {
                            projectId,
                        }, trans)
                    )}
                </>
            ) : (
                <CircularProgress />
            )}
        </div>
    );
};
